/*
 * Printeo — tri et pages des avis clients (lot C, snippet pt-avis-liste). Vanilla, chargé en defer.
 *
 * - Le serveur rend la page 1 dans l'ordre par défaut : avis avec photos d'abord, puis du plus
 *   récent au plus ancien (même règle qu'ici : date décroissante, puis handle décroissant).
 * - Tri : plus récents, plus anciens, note haute, note basse ; case « Avis avec photos en
 *   premier » (cochée au chargement).
 * - Pages : flèches et numéros, sans quitter ni recharger la page. Sur téléphone, les numéros
 *   se resserrent (première, courante, dernière) pour tenir en 320 px. Au changement de page :
 *   retour en haut de la liste, focus sur la liste, annonce « Page X sur Y ».
 * - Au premier tri ou au premier changement de page, tous les avis sont chargés depuis
 *   [data-source]?view=donnees-avis (250 par appel, « suivant » pour la suite), puis les cartes
 *   sont reconstruites depuis le <template data-pt-avis-modele>.
 * Aucun texte affiché n'est écrit ici : tout vient des attributs data-* et du modèle rendus par
 * le serveur. Aucun balisage d'avis n'est produit (garde-fou SPEC §6).
 */
(() => {
  'use strict';

  if (window.PTAvisCharge) return;
  window.PTAvisCharge = true;

  const mqTelephone = window.matchMedia('(max-width: 749px)');

  const remplacer = (modele, valeurs) => Object.entries(valeurs)
    .reduce((texte, [cle, valeur]) => texte.split(`[${cle}]`).join(String(valeur)), modele || '');

  const recent = (a, b) => b.d.localeCompare(a.d) || b.h.localeCompare(a.h);
  const COMPARER = {
    recent,
    ancien: (a, b) => a.d.localeCompare(b.d) || a.h.localeCompare(b.h),
    'note-haute': (a, b) => b.n - a.n || recent(a, b),
    'note-basse': (a, b) => a.n - b.n || recent(a, b)
  };

  /*
   * Numéros affichés. null = points de suspension.
   * Ordinateur : première, dernière, courante et ses deux voisines ; un trou d'une seule page
   * est comblé. Téléphone : première, dernière, courante (et sa voisine aux extrémités), sans
   * combler : la rangée tient en 320 px (flèches comprises).
   */
  function numeros(courante, derniere, telephone) {
    const voisines = telephone ? (courante === 1 || courante === derniere ? 1 : 0) : 1;
    const gardees = new Set([1, derniere]);
    for (let n = courante - voisines; n <= courante + voisines; n += 1) {
      if (n >= 1 && n <= derniere) gardees.add(n);
    }
    const triees = [...gardees].sort((a, b) => a - b);
    const sortie = [];
    triees.forEach((n, i) => {
      const ecart = i ? n - triees[i - 1] : 1;
      if (ecart === 2 && !telephone) sortie.push(n - 1);
      else if (ecart >= 2) sortie.push(null);
      sortie.push(n);
    });
    return sortie;
  }

  async function chargerTout(source) {
    const avis = [];
    const avecVue = (adresse) => {
      const url = new URL(adresse, window.location.origin);
      url.searchParams.set('view', 'donnees-avis');
      return url.toString();
    };
    let url = avecVue(source);
    for (let i = 0; url && i < 40; i += 1) {
      const reponse = await fetch(url, { credentials: 'same-origin' });
      if (!reponse.ok) throw new Error(`HTTP ${reponse.status}`);
      const donnees = JSON.parse(await reponse.text());
      avis.push(...(donnees.avis || []));
      url = donnees.suivant ? avecVue(donnees.suivant) : null;
    }
    return avis.map((a) => ({
      h: String(a.h || ''),
      n: Number(a.n) || 0,
      d: String(a.d || ''),
      f: String(a.f || ''),
      a: String(a.a || ''),
      t: String(a.t || ''),
      r: String(a.r || ''),
      p: Array.isArray(a.p) ? a.p : []
    }));
  }

  function initialiser(racine) {
    if (racine.dataset.ptAvisPret) return;
    const liste = racine.querySelector('[data-pt-avis-liste]');
    const modele = racine.querySelector('[data-pt-avis-modele]');
    const outils = racine.querySelector('[data-pt-avis-outils]');
    const choixTri = racine.querySelector('[data-pt-avis-tri]');
    const casePhotos = racine.querySelector('[data-pt-avis-photos]');
    const pagination = racine.querySelector('[data-pt-avis-pages]');
    const numerosListe = racine.querySelector('[data-pt-avis-numeros]');
    const fleches = Array.from(racine.querySelectorAll('[data-pt-avis-pas]'));
    const annonce = racine.querySelector('[data-pt-avis-annonce]');
    const erreur = racine.querySelector('[data-pt-avis-erreur]');
    const source = racine.dataset.source;
    const parPage = Number(racine.dataset.parPage) || 10;
    if (!liste || !modele || !source || !choixTri || !casePhotos) return;
    racine.dataset.ptAvisPret = 'oui';

    let donnees = null;
    let chargement = null;
    let ordre = null;
    let page = 1;
    let nbPages = Math.max(1, Math.ceil((Number(racine.dataset.total) || 0) / parPage));

    if (outils) outils.hidden = false;

    const charger = () => {
      if (!chargement) {
        chargement = chargerTout(source).then((avis) => { donnees = avis; return avis; });
        chargement.catch(() => { chargement = null; });
      }
      return chargement;
    };

    const ordonner = () => {
      const tries = [...donnees].sort(COMPARER[choixTri.value] || recent);
      if (!casePhotos.checked) return tries;
      return [...tries.filter((a) => a.p.length), ...tries.filter((a) => !a.p.length)];
    };

    const ecrireLignes = (element, texte) => {
      texte.split('\n').forEach((ligne, i) => {
        if (i) element.append(document.createElement('br'));
        element.append(ligne);
      });
    };

    function carte(avis) {
      const noeud = modele.content.firstElementChild.cloneNode(true);
      const champ = (nom) => noeud.querySelector(`[data-champ="${nom}"]`);
      const note = Math.max(0, Math.min(5, avis.n));
      const etoiles = noeud.querySelector('.pt-etoiles');
      if (etoiles) {
        etoiles.style.setProperty('--pt-note', String(note));
        etoiles.dataset.note = String(note);
        etoiles.setAttribute('aria-label', remplacer(racine.dataset.noteModele, { n: String(note).replace('.', ',') }));
      }

      const auteur = avis.a.trim();
      if (auteur) champ('auteur').textContent = auteur;
      else champ('auteur').remove();

      const date = champ('date');
      if (avis.d) {
        date.dateTime = avis.d;
        date.textContent = avis.f || avis.d;
      } else date.remove();

      if (avis.t.trim()) ecrireLignes(champ('texte'), avis.t.trim());
      else champ('texte').remove();

      const photos = champ('photos');
      if (avis.p.length) {
        avis.p.forEach((photo, i) => {
          const element = document.createElement('li');
          const lien = document.createElement('a');
          lien.className = 'pt-avis__photo';
          lien.href = photo.g;
          lien.dataset.ptAgrandir = '';
          lien.dataset.ptGroupe = avis.h;
          lien.dataset.alt = photo.a || remplacer(racine.dataset.photoAlt, { n: i + 1 });
          const image = document.createElement('img');
          image.className = 'pt-avis__vignette';
          image.src = photo.m;
          image.width = 150;
          image.height = 150;
          image.loading = 'lazy';
          image.decoding = 'async';
          image.alt = '';
          const libelle = document.createElement('span');
          libelle.className = 'pt-visuellement-cache';
          libelle.textContent = remplacer(racine.dataset.photoOuvrir, { n: i + 1 });
          lien.append(image, libelle);
          element.append(lien);
          photos.append(element);
        });
      } else photos.remove();

      if (avis.r.trim()) ecrireLignes(champ('reponse'), avis.r.trim());
      else champ('reponse-bloc').remove();
      return noeud;
    }

    function construirePages() {
      if (!pagination || !numerosListe) return;
      pagination.hidden = nbPages < 2;
      fleches.forEach((fleche) => {
        const cible = page + Number(fleche.dataset.ptAvisPas);
        fleche.disabled = cible < 1 || cible > nbPages;
      });
      numerosListe.replaceChildren(...numeros(page, nbPages, mqTelephone.matches).map((n) => {
        const element = document.createElement('li');
        if (n === null) {
          element.className = 'pt-avis-pages__ellipse';
          element.setAttribute('aria-hidden', 'true');
          element.textContent = '…';
          return element;
        }
        const bouton = document.createElement('button');
        bouton.type = 'button';
        bouton.className = 'pt-avis-pages__numero';
        bouton.dataset.ptAvisPage = String(n);
        bouton.textContent = String(n);
        bouton.setAttribute('aria-label', remplacer(pagination.dataset.page, { n }));
        if (n === page) bouton.setAttribute('aria-current', 'page');
        element.append(bouton);
        return element;
      }));
    }

    function afficher() {
      nbPages = Math.max(1, Math.ceil(ordre.length / parPage));
      page = Math.min(Math.max(1, page), nbPages);
      liste.replaceChildren(...ordre.slice((page - 1) * parPage, page * parPage).map(carte));
      construirePages();
      if (annonce) annonce.textContent = remplacer(racine.dataset.statutPage, { n: page, t: nbPages });
    }

    async function appliquer({ nouvellePage = 1, retrier = false, remonter = false } = {}) {
      if (erreur) erreur.hidden = true;
      liste.setAttribute('aria-busy', 'true');
      racine.classList.add('pt-avis-bloc--chargement');
      try {
        await charger();
        if (retrier || !ordre) ordre = ordonner();
        page = nouvellePage;
        afficher();
        if (remonter) {
          // Retour immédiat en haut de la liste (une animation sur plusieurs milliers de pixels
          // retarderait la lecture) ; la marge d'ancre tient compte des barres collantes.
          liste.scrollIntoView({ behavior: 'instant', block: 'start' });
          liste.focus({ preventScroll: true });
        }
      } catch (e) {
        if (erreur) erreur.hidden = false;
      } finally {
        liste.removeAttribute('aria-busy');
        racine.classList.remove('pt-avis-bloc--chargement');
      }
    }

    // Préchargement discret des avis quand le bloc approche de l'écran : le premier tri ou le
    // premier changement de page n'attend pas le réseau.
    if ('IntersectionObserver' in window) {
      const veille = new IntersectionObserver((entrees) => {
        if (entrees.some((entree) => entree.isIntersecting)) {
          veille.disconnect();
          charger().catch(() => {});
        }
      }, { rootMargin: '600px 0px' });
      veille.observe(racine);
    }

    construirePages();
    choixTri.addEventListener('change', () => appliquer({ retrier: true }));
    casePhotos.addEventListener('change', () => appliquer({ retrier: true }));
    fleches.forEach((fleche) => {
      fleche.addEventListener('click', () => appliquer({ nouvellePage: page + Number(fleche.dataset.ptAvisPas), remonter: true }));
    });
    if (numerosListe) {
      numerosListe.addEventListener('click', (evenement) => {
        const bouton = evenement.target.closest('[data-pt-avis-page]');
        if (bouton && Number(bouton.dataset.ptAvisPage) !== page) {
          appliquer({ nouvellePage: Number(bouton.dataset.ptAvisPage), remonter: true });
        }
      });
    }
    mqTelephone.addEventListener('change', construirePages);
  }

  const tout = (racine = document) => racine.querySelectorAll('[data-pt-avis-bloc]').forEach(initialiser);
  tout();
  document.addEventListener('shopify:section:load', (evenement) => tout(evenement.target));
})();
